module Widgets.Modal where

import Common

import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Internal.ConcurUtil (loopInfinitely)


data ModalResult a
    = ModalClosed
    -- ^ The modal was closed by a user clicking outside it.
    | ModalReturned a


-- | A modal which wraps an internal widget and closes either when the user
-- | clicks outside it or when the internal widget returns a result.
-- |
-- | This function expects an "outer" widget to be passed, which should render
-- | the page that triggered the modal so that it remains in the DOM. This will
-- | be rendered with return values ignored.
modalWidget :: forall a b. Widget HTML b -> Widget HTML a -> Widget HTML (ModalResult a)
modalWidget outer inner = modalWidget' outer (ModalReturned <$> inner)

-- | A more advanced version of `modalWidget` which allows the widget inside
-- | the modal to close itself by returning `ModalClosed`.
modalWidget' :: forall a b. Widget HTML b -> Widget HTML (ModalResult a) -> Widget HTML (ModalResult a)
modalWidget' outer inner = loopInfinitely outer <|> D.div
    [ P.className "modal" ]
    [ inner
    , D.a
        [ ModalClosed <$ P.onClick, P.className "modal-close" ]
        [ D.text "Close" ]
    ]

-- | Loops a modal's state until it's closed, then returns the last state.
loopModal :: forall a b. Widget HTML b -> (a -> Widget HTML (ModalResult a)) -> a -> Widget HTML a
loopModal outer inner state = do
    ret <- modalWidget' outer (inner state) 
    case ret of
        ModalReturned state' -> loopModal outer inner state'
        ModalClosed -> pure state


-- | Displays a modal which may return an error. If an error is returned, this
-- | function catches it and displays an error message in the modal, before
-- | returning `ModalClosed` when the user acknowledges it.
errorWrapperModal :: forall a b.
    Widget HTML b
    -> Widget HTML (Either ErrorWrapper a)
    -> Widget HTML (ModalResult a)
errorWrapperModal outer inner = modalWidget outer inner >>= case _ of
    ModalClosed -> pure ModalClosed
    ModalReturned (Right res) -> pure $ ModalReturned res
    ModalReturned (Left err) -> modalWidget' outer $ errorModalWidget err


errorModalWidget :: forall a err. Show err => err -> Widget HTML (ModalResult a)
errorModalWidget err = do
    _ <- D.div'
        [ D.h1' [ D.text $ lookupTranslation "error-occurred" ]
        , D.p' [ D.text $ show err ]
        , D.button [ 0 <$ P.onClick ] [ D.text $ lookupTranslation "ok" ]
        ]
    pure ModalClosed
